import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

export type OnboardingOption = {
  value: string;
  label: string;
  ack: string;
};

export type OnboardingStep = {
  id: string;
  question: string;
  options: OnboardingOption[];
};

export type OnboardingCopy = {
  title: string;
  subtitle: string;
  noTestNote: string;
  mascotName: string;
  mascotLine1: string;
  mascotLine2: string;
  progressLabel: string;
  skipStep: string;
  skipAll: string;
  continue: string;
  saveProgress: string;
  soundOn: string;
  soundOff: string;
  doneTitle: string;
  doneDescription: string;
  continueNow: string;
  saveAndContinue: string;
  editAnswers: string;
  steps: OnboardingStep[];
};

export const onboardingCopyByLocale = defineLocaleRecord<OnboardingCopy>({
  en: {
    title: "Let’s set this up in one minute.",
    subtitle: "A few quick questions so we can keep your next steps clear.",
    noTestNote: "This is not a test. No wrong answers. Skip anything.",
    mascotName: "Zip",
    mascotLine1: "I’ll keep this simple.",
    mascotLine2: "Tell me what feels closest.",
    progressLabel: "Step {current} of {total}",
    skipStep: "Skip step",
    skipAll: "Skip for now",
    continue: "Continue",
    saveProgress: "Save progress",
    soundOn: "Sound on",
    soundOff: "Sound off",
    doneTitle: "You’re ready.",
    doneDescription: "We’ll use this to keep your path clear and practical.",
    continueNow: "Continue now",
    saveAndContinue: "Save progress and continue",
    editAnswers: "Edit answers",
    steps: [
      {
        id: "intent",
        question: "What do you want to make right now?",
        options: [
          { value: "website", label: "A website", ack: "Got it." },
          { value: "app", label: "An app", ack: "Makes sense." },
          { value: "bot", label: "A bot", ack: "Okay." },
          { value: "video", label: "A video", ack: "Understood." },
          { value: "exploring", label: "Just exploring", ack: "No problem." },
        ],
      },
      {
        id: "friction",
        question: "What is stopping you the most?",
        options: [
          { value: "start", label: "I don’t know where to start", ack: "That makes sense." },
          { value: "tools", label: "Too many tools", ack: "Then we’ll narrow it down." },
          { value: "time", label: "No time to figure things out", ack: "Then we keep this short." },
          { value: "stopped", label: "I tried before and stopped", ack: "Then we keep this simple." },
        ],
      },
      {
        id: "time",
        question: "How much time do you realistically have?",
        options: [
          { value: "15-30", label: "15–30 minutes", ack: "Okay." },
          { value: "evenings", label: "A few evenings", ack: "Good to know." },
          { value: "weekend", label: "One weekend", ack: "Makes sense." },
          { value: "unclear", label: "Not sure yet", ack: "No pressure." },
        ],
      },
      {
        id: "ai",
        question: "Have you tried AI tools before?",
        options: [
          { value: "opened-closed", label: "Opened them, closed them", ack: "Got it." },
          { value: "confused", label: "Used them, got confused", ack: "That’s common." },
          { value: "not-yet", label: "Not yet", ack: "No problem." },
          { value: "never", label: "Never", ack: "We’ll keep it clear." },
        ],
      },
      {
        id: "next-step",
        question: "What sounds better for your next step?",
        options: [
          { value: "plan", label: "One clear plan", ack: "Then we keep it focused." },
          { value: "example", label: "One example to copy", ack: "Then we keep it practical." },
          { value: "checklist", label: "A short checklist", ack: "Then we keep it simple." },
          { value: "start-now", label: "Just show me where to begin", ack: "Then we start now." },
        ],
      },
    ],
  },
  fr: {
    title: "Configurons cela en une minute.",
    subtitle: "Quelques questions rapides pour garder la suite claire.",
    noTestNote: "Ce n'est pas un test. Pas de mauvaise reponse. Vous pouvez passer.",
    mascotName: "Zip",
    mascotLine1: "Je vais faire simple.",
    mascotLine2: "Dites ce qui vous ressemble le plus.",
    progressLabel: "Etape {current} sur {total}",
    skipStep: "Passer l'etape",
    skipAll: "Passer pour l'instant",
    continue: "Continuer",
    saveProgress: "Sauvegarder ma progression",
    soundOn: "Son active",
    soundOff: "Son coupe",
    doneTitle: "C'est pret.",
    doneDescription: "Nous utiliserons cela pour garder un chemin clair et concret.",
    continueNow: "Continuer maintenant",
    saveAndContinue: "Sauvegarder et continuer",
    editAnswers: "Modifier mes reponses",
    steps: [
      {
        id: "intent",
        question: "Que voulez-vous creer maintenant ?",
        options: [
          { value: "website", label: "Un site web", ack: "Compris." },
          { value: "app", label: "Une application", ack: "D'accord." },
          { value: "bot", label: "Un bot", ack: "Tres bien." },
          { value: "video", label: "Une video", ack: "Compris." },
          { value: "exploring", label: "J'explore", ack: "Pas de souci." },
        ],
      },
      {
        id: "friction",
        question: "Qu'est-ce qui vous bloque le plus ?",
        options: [
          { value: "start", label: "Je ne sais pas par ou commencer", ack: "C'est clair." },
          { value: "tools", label: "Trop d'outils", ack: "Alors on reduit les choix." },
          { value: "time", label: "Je n'ai pas le temps de tout comprendre", ack: "Alors on fera court." },
          { value: "stopped", label: "J'ai essaye puis j'ai arrete", ack: "Alors on garde cela simple." },
        ],
      },
      {
        id: "time",
        question: "Combien de temps avez-vous vraiment ?",
        options: [
          { value: "15-30", label: "15-30 minutes", ack: "D'accord." },
          { value: "evenings", label: "Quelques soirees", ack: "Bien note." },
          { value: "weekend", label: "Un week-end", ack: "Compris." },
          { value: "unclear", label: "Je ne sais pas encore", ack: "Aucune pression." },
        ],
      },
      {
        id: "ai",
        question: "Avez-vous deja essaye des outils IA ?",
        options: [
          { value: "opened-closed", label: "Ouverts puis fermes", ack: "Compris." },
          { value: "confused", label: "Utilises puis confusion", ack: "C'est frequent." },
          { value: "not-yet", label: "Pas encore", ack: "Pas de souci." },
          { value: "never", label: "Jamais", ack: "On gardera cela clair." },
        ],
      },
      {
        id: "next-step",
        question: "Qu'est-ce qui vous convient le mieux pour la suite ?",
        options: [
          { value: "plan", label: "Un plan clair", ack: "Alors on reste focalise." },
          { value: "example", label: "Un exemple a copier", ack: "Alors on reste concret." },
          { value: "checklist", label: "Une courte checklist", ack: "Alors on reste simple." },
          { value: "start-now", label: "Montrez-moi juste par ou commencer", ack: "Alors on commence maintenant." },
        ],
      },
    ],
  },
  ru: {
    title: "Настроим это за одну минуту.",
    subtitle: "Пара коротких вопросов, чтобы следующий шаг был понятным.",
    noTestNote: "Это не тест. Неправильных ответов нет. Любой шаг можно пропустить.",
    mascotName: "Zip",
    mascotLine1: "Я сделаю это простым.",
    mascotLine2: "Выберите то, что ближе вам.",
    progressLabel: "Шаг {current} из {total}",
    skipStep: "Пропустить шаг",
    skipAll: "Пропустить пока",
    continue: "Продолжить",
    saveProgress: "Сохранить прогресс",
    soundOn: "Звук включен",
    soundOff: "Звук выключен",
    doneTitle: "Готово.",
    doneDescription: "Мы используем это, чтобы путь был ясным и практичным.",
    continueNow: "Продолжить сейчас",
    saveAndContinue: "Сохранить и продолжить",
    editAnswers: "Изменить ответы",
    steps: [
      {
        id: "intent",
        question: "Что вы хотите сделать прямо сейчас?",
        options: [
          { value: "website", label: "Сайт", ack: "Понял." },
          { value: "app", label: "Приложение", ack: "Хорошо." },
          { value: "bot", label: "Бот", ack: "Принято." },
          { value: "video", label: "Видео", ack: "Понятно." },
          { value: "exploring", label: "Пока просто смотрю", ack: "Без проблем." },
        ],
      },
      {
        id: "friction",
        question: "Что мешает вам больше всего?",
        options: [
          { value: "start", label: "Не понимаю, с чего начать", ack: "Это понятно." },
          { value: "tools", label: "Слишком много инструментов", ack: "Тогда сузим выбор." },
          { value: "time", label: "Нет времени разбираться", ack: "Тогда будет коротко." },
          { value: "stopped", label: "Я уже пробовал и остановился", ack: "Тогда сделаем проще." },
        ],
      },
      {
        id: "time",
        question: "Сколько времени у вас реально есть?",
        options: [
          { value: "15-30", label: "15-30 минут", ack: "Хорошо." },
          { value: "evenings", label: "Несколько вечеров", ack: "Понял." },
          { value: "weekend", label: "Один выходной", ack: "Принято." },
          { value: "unclear", label: "Пока не знаю", ack: "Без давления." },
        ],
      },
      {
        id: "ai",
        question: "Вы уже пробовали инструменты ИИ?",
        options: [
          { value: "opened-closed", label: "Открыл и закрыл", ack: "Понял." },
          { value: "confused", label: "Пробовал, но запутался", ack: "Это бывает часто." },
          { value: "not-yet", label: "Еще нет", ack: "Без проблем." },
          { value: "never", label: "Никогда", ack: "Будет максимально ясно." },
        ],
      },
      {
        id: "next-step",
        question: "Какой следующий шаг вам ближе?",
        options: [
          { value: "plan", label: "Один понятный план", ack: "Тогда держим фокус." },
          { value: "example", label: "Один пример для копирования", ack: "Тогда идем практично." },
          { value: "checklist", label: "Короткий чеклист", ack: "Тогда делаем просто." },
          { value: "start-now", label: "Просто покажите, с чего начать", ack: "Тогда начнем сразу." },
        ],
      },
    ],
  },
  uk: {
    title: "Налаштуємо це за одну хвилину.",
    subtitle: "Кілька коротких питань, щоб наступний крок був зрозумілим.",
    noTestNote: "Це не тест. Немає неправильних відповідей. Будь-який крок можна пропустити.",
    mascotName: "Zip",
    mascotLine1: "Я зроблю це простим.",
    mascotLine2: "Оберіть те, що ближче вам.",
    progressLabel: "Крок {current} з {total}",
    skipStep: "Пропустити крок",
    skipAll: "Пропустити поки",
    continue: "Продовжити",
    saveProgress: "Зберегти прогрес",
    soundOn: "Звук увімкнено",
    soundOff: "Звук вимкнено",
    doneTitle: "Готово.",
    doneDescription: "Ми використаємо це, щоб шлях був ясним і практичним.",
    continueNow: "Продовжити зараз",
    saveAndContinue: "Зберегти і продовжити",
    editAnswers: "Змінити відповіді",
    steps: [
      {
        id: "intent",
        question: "Що ви хочете зробити прямо зараз?",
        options: [
          { value: "website", label: "Сайт", ack: "Зрозуміло." },
          { value: "app", label: "Застосунок", ack: "Добре." },
          { value: "bot", label: "Бот", ack: "Прийнято." },
          { value: "video", label: "Відео", ack: "Зрозуміло." },
          { value: "exploring", label: "Поки просто дивлюсь", ack: "Без проблем." },
        ],
      },
      {
        id: "friction",
        question: "Що зупиняє вас найбільше?",
        options: [
          { value: "start", label: "Не знаю, з чого почати", ack: "Це зрозуміло." },
          { value: "tools", label: "Занадто багато інструментів", ack: "Тоді звузимо вибір." },
          { value: "time", label: "Немає часу розбиратися", ack: "Тоді буде коротко." },
          { value: "stopped", label: "Я вже пробував і зупинився", ack: "Тоді зробимо простіше." },
        ],
      },
      {
        id: "time",
        question: "Скільки часу у вас реально є?",
        options: [
          { value: "15-30", label: "15-30 хвилин", ack: "Добре." },
          { value: "evenings", label: "Кілька вечорів", ack: "Прийнято." },
          { value: "weekend", label: "Один вікенд", ack: "Зрозуміло." },
          { value: "unclear", label: "Поки не знаю", ack: "Без тиску." },
        ],
      },
      {
        id: "ai",
        question: "Ви вже пробували інструменти ШІ?",
        options: [
          { value: "opened-closed", label: "Відкрив і закрив", ack: "Зрозуміло." },
          { value: "confused", label: "Пробував, але заплутався", ack: "Це нормально." },
          { value: "not-yet", label: "Ще ні", ack: "Без проблем." },
          { value: "never", label: "Ніколи", ack: "Тоді все буде максимально ясно." },
        ],
      },
      {
        id: "next-step",
        question: "Який наступний крок вам ближчий?",
        options: [
          { value: "plan", label: "Один чіткий план", ack: "Тоді тримаємо фокус." },
          { value: "example", label: "Один приклад для копіювання", ack: "Тоді йдемо практично." },
          { value: "checklist", label: "Короткий чеклист", ack: "Тоді робимо просто." },
          { value: "start-now", label: "Просто покажіть, з чого почати", ack: "Тоді почнемо зараз." },
        ],
      },
    ],
  },
  de: {
    title: "Lass uns das in einer Minute einstellen.",
    subtitle: "Ein paar kurze Fragen, damit der nächste Schritt klar ist.",
    noTestNote: "Das ist kein Test. Es gibt keine falschen Antworten. Du kannst alles überspringen.",
    mascotName: "Zip",
    mascotLine1: "Ich halte es einfach.",
    mascotLine2: "Wähle, was sich für dich richtig anfühlt.",
    progressLabel: "Schritt {current} von {total}",
    skipStep: "Schritt überspringen",
    skipAll: "Jetzt überspringen",
    continue: "Weiter",
    saveProgress: "Fortschritt speichern",
    soundOn: "Ton an",
    soundOff: "Ton aus",
    doneTitle: "Fertig.",
    doneDescription: "Wir nutzen das, damit dein Weg klar und praktisch bleibt.",
    continueNow: "Jetzt fortfahren",
    saveAndContinue: "Speichern und fortfahren",
    editAnswers: "Antworten ändern",
    steps: [
      {
        id: "intent",
        question: "Was möchtest du jetzt bauen?",
        options: [
          { value: "website", label: "Eine Website", ack: "Verstanden." },
          { value: "app", label: "Eine App", ack: "Okay." },
          { value: "bot", label: "Einen Bot", ack: "Alles klar." },
          { value: "video", label: "Ein Video", ack: "Verstanden." },
          { value: "exploring", label: "Ich schaue mich nur um", ack: "Kein Problem." },
        ],
      },
      {
        id: "friction",
        question: "Was blockiert dich am meisten?",
        options: [
          { value: "start", label: "Ich weiß nicht, wo ich anfangen soll", ack: "Das ergibt Sinn." },
          { value: "tools", label: "Zu viele Tools", ack: "Dann grenzen wir es ein." },
          { value: "time", label: "Keine Zeit zum Ausprobieren", ack: "Dann halten wir es kurz." },
          { value: "stopped", label: "Ich habe es versucht und aufgehört", ack: "Dann machen wir es einfach." },
        ],
      },
      {
        id: "time",
        question: "Wie viel Zeit hast du realistisch?",
        options: [
          { value: "15-30", label: "15-30 Minuten", ack: "Okay." },
          { value: "evenings", label: "Ein paar Abende", ack: "Gut zu wissen." },
          { value: "weekend", label: "Ein Wochenende", ack: "Verstanden." },
          { value: "unclear", label: "Noch unklar", ack: "Kein Druck." },
        ],
      },
      {
        id: "ai",
        question: "Hast du AI-Tools schon ausprobiert?",
        options: [
          { value: "opened-closed", label: "Geöffnet und wieder geschlossen", ack: "Verstanden." },
          { value: "confused", label: "Genutzt und verwirrt gewesen", ack: "Das passiert oft." },
          { value: "not-yet", label: "Noch nicht", ack: "Kein Problem." },
          { value: "never", label: "Nie", ack: "Dann halten wir es klar." },
        ],
      },
      {
        id: "next-step",
        question: "Was passt besser als nächster Schritt?",
        options: [
          { value: "plan", label: "Ein klarer Plan", ack: "Dann bleiben wir fokussiert." },
          { value: "example", label: "Ein Beispiel zum Nachmachen", ack: "Dann bleiben wir praktisch." },
          { value: "checklist", label: "Eine kurze Checkliste", ack: "Dann bleibt es einfach." },
          { value: "start-now", label: "Zeig mir einfach den Start", ack: "Dann starten wir jetzt." },
        ],
      },
    ],
  },
  es: {
    title: "Vamos a configurar esto en un minuto.",
    subtitle: "Unas preguntas cortas para mantener claro tu siguiente paso.",
    noTestNote: "Esto no es un test. No hay respuestas incorrectas. Puedes omitir cualquier paso.",
    mascotName: "Zip",
    mascotLine1: "Lo mantendre simple.",
    mascotLine2: "Elige lo que se sienta mas cercano.",
    progressLabel: "Paso {current} de {total}",
    skipStep: "Omitir paso",
    skipAll: "Omitir por ahora",
    continue: "Continuar",
    saveProgress: "Guardar progreso",
    soundOn: "Sonido activo",
    soundOff: "Sonido desactivado",
    doneTitle: "Listo.",
    doneDescription: "Usaremos esto para mantener tu camino claro y practico.",
    continueNow: "Continuar ahora",
    saveAndContinue: "Guardar y continuar",
    editAnswers: "Editar respuestas",
    steps: [
      {
        id: "intent",
        question: "Que quieres crear ahora mismo?",
        options: [
          { value: "website", label: "Un sitio web", ack: "Entendido." },
          { value: "app", label: "Una app", ack: "Perfecto." },
          { value: "bot", label: "Un bot", ack: "Vale." },
          { value: "video", label: "Un video", ack: "Entendido." },
          { value: "exploring", label: "Solo explorando", ack: "Sin problema." },
        ],
      },
      {
        id: "friction",
        question: "Que te frena mas?",
        options: [
          { value: "start", label: "No se por donde empezar", ack: "Tiene sentido." },
          { value: "tools", label: "Demasiadas herramientas", ack: "Entonces lo reducimos." },
          { value: "time", label: "No tengo tiempo para entender todo", ack: "Entonces sera corto." },
          { value: "stopped", label: "Lo intente y lo deje", ack: "Entonces lo simplificamos." },
        ],
      },
      {
        id: "time",
        question: "Cuanto tiempo tienes de forma realista?",
        options: [
          { value: "15-30", label: "15-30 minutos", ack: "Vale." },
          { value: "evenings", label: "Algunas noches", ack: "Anotado." },
          { value: "weekend", label: "Un fin de semana", ack: "Entendido." },
          { value: "unclear", label: "Aun no lo se", ack: "Sin presion." },
        ],
      },
      {
        id: "ai",
        question: "Has probado herramientas de IA antes?",
        options: [
          { value: "opened-closed", label: "Las abri y las cerre", ack: "Entendido." },
          { value: "confused", label: "Las use y me confundi", ack: "Eso es comun." },
          { value: "not-yet", label: "Aun no", ack: "Sin problema." },
          { value: "never", label: "Nunca", ack: "Lo mantendremos claro." },
        ],
      },
      {
        id: "next-step",
        question: "Que suena mejor para tu siguiente paso?",
        options: [
          { value: "plan", label: "Un plan claro", ack: "Entonces mantenemos foco." },
          { value: "example", label: "Un ejemplo para copiar", ack: "Entonces vamos a lo practico." },
          { value: "checklist", label: "Una checklist corta", ack: "Entonces sera simple." },
          { value: "start-now", label: "Solo muestrame por donde empezar", ack: "Entonces empezamos ahora." },
        ],
      },
    ],
  },
  it: {
    title: "Impostiamo tutto in un minuto.",
    subtitle: "Poche domande rapide per rendere chiaro il prossimo passo.",
    noTestNote: "Non e un test. Non ci sono risposte sbagliate. Puoi saltare ogni passaggio.",
    mascotName: "Zip",
    mascotLine1: "Lo tengo semplice.",
    mascotLine2: "Scegli cio che senti piu vicino.",
    progressLabel: "Passo {current} di {total}",
    skipStep: "Salta passaggio",
    skipAll: "Salta per ora",
    continue: "Continua",
    saveProgress: "Salva progressi",
    soundOn: "Audio attivo",
    soundOff: "Audio disattivo",
    doneTitle: "Fatto.",
    doneDescription: "Useremo queste risposte per mantenere il percorso chiaro e pratico.",
    continueNow: "Continua ora",
    saveAndContinue: "Salva e continua",
    editAnswers: "Modifica risposte",
    steps: [
      {
        id: "intent",
        question: "Cosa vuoi creare adesso?",
        options: [
          { value: "website", label: "Un sito web", ack: "Capito." },
          { value: "app", label: "Un'app", ack: "Va bene." },
          { value: "bot", label: "Un bot", ack: "Perfetto." },
          { value: "video", label: "Un video", ack: "Chiaro." },
          { value: "exploring", label: "Sto solo esplorando", ack: "Nessun problema." },
        ],
      },
      {
        id: "friction",
        question: "Cosa ti blocca di piu?",
        options: [
          { value: "start", label: "Non so da dove iniziare", ack: "Ha senso." },
          { value: "tools", label: "Troppi strumenti", ack: "Allora restringiamo le scelte." },
          { value: "time", label: "Non ho tempo per capire tutto", ack: "Allora restiamo brevi." },
          { value: "stopped", label: "Ho provato e mi sono fermato", ack: "Allora lo teniamo semplice." },
        ],
      },
      {
        id: "time",
        question: "Quanto tempo hai realisticamente?",
        options: [
          { value: "15-30", label: "15-30 minuti", ack: "Ok." },
          { value: "evenings", label: "Qualche sera", ack: "Buono a sapersi." },
          { value: "weekend", label: "Un weekend", ack: "Chiaro." },
          { value: "unclear", label: "Non lo so ancora", ack: "Nessuna pressione." },
        ],
      },
      {
        id: "ai",
        question: "Hai gia provato strumenti AI?",
        options: [
          { value: "opened-closed", label: "Aperti e chiusi", ack: "Capito." },
          { value: "confused", label: "Usati ma con confusione", ack: "Succede spesso." },
          { value: "not-yet", label: "Non ancora", ack: "Nessun problema." },
          { value: "never", label: "Mai", ack: "Allora restiamo molto chiari." },
        ],
      },
      {
        id: "next-step",
        question: "Cosa preferisci come prossimo passo?",
        options: [
          { value: "plan", label: "Un piano chiaro", ack: "Allora manteniamo il focus." },
          { value: "example", label: "Un esempio da copiare", ack: "Allora andiamo sul pratico." },
          { value: "checklist", label: "Una checklist breve", ack: "Allora resta semplice." },
          { value: "start-now", label: "Mostrami solo da dove iniziare", ack: "Allora iniziamo ora." },
        ],
      },
    ],
  },
  pt: {
    title: "Vamos configurar isso em um minuto.",
    subtitle: "Algumas perguntas rapidas para manter seu proximo passo claro.",
    noTestNote: "Isso nao e um teste. Nao existem respostas erradas. Pule o que quiser.",
    mascotName: "Zip",
    mascotLine1: "Vou manter isso simples.",
    mascotLine2: "Escolha o que parece mais proximo.",
    progressLabel: "Passo {current} de {total}",
    skipStep: "Pular passo",
    skipAll: "Pular por enquanto",
    continue: "Continuar",
    saveProgress: "Salvar progresso",
    soundOn: "Som ligado",
    soundOff: "Som desligado",
    doneTitle: "Pronto.",
    doneDescription: "Vamos usar isso para manter seu caminho claro e pratico.",
    continueNow: "Continuar agora",
    saveAndContinue: "Salvar e continuar",
    editAnswers: "Editar respostas",
    steps: [
      {
        id: "intent",
        question: "O que voce quer criar agora?",
        options: [
          { value: "website", label: "Um site", ack: "Entendi." },
          { value: "app", label: "Um app", ack: "Faz sentido." },
          { value: "bot", label: "Um bot", ack: "Certo." },
          { value: "video", label: "Um video", ack: "Entendido." },
          { value: "exploring", label: "Estou so explorando", ack: "Sem problema." },
        ],
      },
      {
        id: "friction",
        question: "O que mais te impede?",
        options: [
          { value: "start", label: "Nao sei por onde comecar", ack: "Faz sentido." },
          { value: "tools", label: "Ferramentas demais", ack: "Entao vamos reduzir as opcoes." },
          { value: "time", label: "Nao tenho tempo pra entender tudo", ack: "Entao vamos ser breves." },
          { value: "stopped", label: "Ja tentei e parei", ack: "Entao vamos simplificar." },
        ],
      },
      {
        id: "time",
        question: "Quanto tempo voce tem de verdade?",
        options: [
          { value: "15-30", label: "15-30 minutos", ack: "Certo." },
          { value: "evenings", label: "Algumas noites", ack: "Bom saber." },
          { value: "weekend", label: "Um fim de semana", ack: "Entendido." },
          { value: "unclear", label: "Ainda nao sei", ack: "Sem pressao." },
        ],
      },
      {
        id: "ai",
        question: "Voce ja experimentou ferramentas de IA?",
        options: [
          { value: "opened-closed", label: "Abri e fechei", ack: "Entendi." },
          { value: "confused", label: "Usei e fiquei confuso", ack: "Isso e comum." },
          { value: "not-yet", label: "Ainda nao", ack: "Sem problema." },
          { value: "never", label: "Nunca", ack: "Vamos manter tudo claro." },
        ],
      },
      {
        id: "next-step",
        question: "O que soa melhor para seu proximo passo?",
        options: [
          { value: "plan", label: "Um plano claro", ack: "Entao vamos manter o foco." },
          { value: "example", label: "Um exemplo pra copiar", ack: "Entao vamos ao pratico." },
          { value: "checklist", label: "Um checklist curto", ack: "Entao vamos simplificar." },
          { value: "start-now", label: "So me mostra por onde comecar", ack: "Entao comecamos agora." },
        ],
      },
    ],
  },
});

export function getOnboardingCopy(lang: Locale): OnboardingCopy {
  return onboardingCopyByLocale[lang];
}
